import { apiClient } from './api';
import {
  StartSessionRequest,
  SessionStartResponse,
  NicheAgentIterationRequest,
  NicheAgentIterationResponse,
  ScientificSession,
  ScientificAgentType,
  AgentVisualization,
} from '../models/scientificAgent';

const SESSION_STORAGE_KEY = 'scientificAgentSessions';

export class ScientificAgentService {
  // API calls
  static async startSession(agentType: ScientificAgentType, request: StartSessionRequest): Promise<SessionStartResponse> {
    const response = await apiClient.post(`/api/${agentType}/session/start`, request);
    return response.data;
  }

  static async iterate(
    agentType: ScientificAgentType, 
    sessionId: string, 
    request: NicheAgentIterationRequest
  ): Promise<NicheAgentIterationResponse> {
    // Evolution agent uses 'transition' instead of 'iterate'
    const endpoint = agentType === 'evolution' 
      ? `/api/${agentType}/session/${sessionId}/transition`
      : `/api/${agentType}/session/${sessionId}/iterate`;
    
    const response = await apiClient.post(endpoint, request);
    const data = response.data;
    
    // Handle genetic agent's image prompt response format
    if (data.generatedPrompt && data.outputType === 'image') {
      // Transform the response into the expected visualization format
      const visualization: AgentVisualization = {
        title: `${data.agentType} - ${data.detailDomain}`,
        domain: data.agentType || 'Genetic Science',
        detailDomain: data.detailDomain || 'Analysis',
        description: 'AI-generated image prompt for visualization',
        keyTerms: data.references || [],
        content: {
          overview: 'Generated an image prompt for visualizing genetic engineering concepts.',
          detailedExplanation: data.generatedPrompt,
          keyInsights: [
            'Advanced genetic engineering visualization',
            'AI-designed regulatory sequences',
            'CRISPR-based gene editing systems',
            'Multi-omics data integration'
          ],
          visualElements: [{
            type: 'Image Prompt',
            description: data.generatedPrompt,
            significance: 'Visual representation of cutting-edge genetic engineering techniques'
          }],
          references: data.references
        },
        timestamp: new Date().toISOString()
      };
      
      return {
        success: data.success,
        errors: data.errors,
        visualization
      };
    }
    
    return data;
  }

  // Local session management
  static createLocalSession(
    agentType: ScientificAgentType,
    apiSessionId: string,
    initialPrompt: string,
    visualization?: AgentVisualization
  ): ScientificSession {
    const session: ScientificSession = {
      id: `scientific-${agentType}-${Date.now()}`,
      sessionId: apiSessionId,
      agentType,
      initialPrompt,
      visualizations: visualization ? [visualization] : [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveSession(session);
    return session;
  }

  static getSession(id: string): ScientificSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === id) || null;
  }

  static getAllSessions(): ScientificSession[] {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static getSessionsByAgent(agentType: ScientificAgentType): ScientificSession[] {
    const sessions = this.getAllSessions();
    return sessions.filter(s => s.agentType === agentType);
  }

  static saveSession(session: ScientificSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }

    session.updatedAt = new Date().toISOString();
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  }

  static deleteSession(id: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== id);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
  }

  static addVisualization(sessionId: string, visualization: AgentVisualization): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.visualizations.push(visualization);
      this.saveSession(session);
    }
  }

  static exportSession(session: ScientificSession): string {
    let content = `# Scientific Analysis: ${session.agentType.replace(/-/g, ' ').toUpperCase()}\n\n`;
    content += `**Initial Prompt:** ${session.initialPrompt}\n`;
    content += `**Created:** ${new Date(session.createdAt).toLocaleString()}\n\n`;

    session.visualizations.forEach((viz, index) => {
      content += `## Analysis ${index + 1}: ${viz.title}\n\n`;
      content += `**Domain:** ${viz.domain}\n`;
      content += `**Detail Domain:** ${viz.detailDomain}\n`;
      content += `**Description:** ${viz.description}\n\n`;

      // Key Terms
      if (viz.keyTerms.length > 0) {
        content += `### Key Terms\n`;
        viz.keyTerms.forEach(term => content += `- ${term}\n`);
        content += '\n';
      }

      // Content
      const c = viz.content;
      content += `### Overview\n${c.overview}\n\n`;
      content += `### Detailed Explanation\n${c.detailedExplanation}\n\n`;

      // Key Insights
      if (c.keyInsights.length > 0) {
        content += `### Key Insights\n`;
        c.keyInsights.forEach(insight => content += `- ${insight}\n`);
        content += '\n';
      }

      // Implications
      if (c.implications && c.implications.length > 0) {
        content += `### Implications\n`;
        c.implications.forEach(impl => content += `- ${impl}\n`);
        content += '\n';
      }

      // Future Directions
      if (c.futureDirections && c.futureDirections.length > 0) {
        content += `### Future Directions\n`;
        c.futureDirections.forEach(dir => content += `- ${dir}\n`);
        content += '\n';
      }

      // Technical Details
      if (c.technicalDetails && c.technicalDetails.length > 0) {
        content += `### Technical Details\n`;
        c.technicalDetails.forEach(detail => {
          content += `**${detail.aspect}:** ${detail.explanation}`;
          if (detail.importance) content += ` (Importance: ${detail.importance})`;
          content += '\n';
        });
        content += '\n';
      }

      // Visual Elements
      if (c.visualElements && c.visualElements.length > 0) {
        content += `### Visual Elements\n`;
        c.visualElements.forEach(elem => {
          content += `**${elem.type}:** ${elem.description}`;
          if (elem.significance) content += ` (Significance: ${elem.significance})`;
          content += '\n';
        });
        content += '\n';
      }

      // References
      if (c.references && c.references.length > 0) {
        content += `### References\n`;
        c.references.forEach(ref => content += `- ${ref}\n`);
        content += '\n';
      }

      content += '---\n\n';
    });

    return content;
  }
}